import { useMemo, useState } from 'react';
import {
  ElectionDesignation,
  EMPLOYMENT_DISCLOSURE,
  PreChargeLimitDecision,
  NOT_EMPLOYED_VALUE,
  RECURRING_DONATION_DISCLOSURE,
  assertNever,
  attestationCopy,
  coverFeesChargeCents,
  donorElectionLabel,
  resolveDonationAmounts,
} from '@signalvote/shared';
import { Check, LockKeyhole, Pencil } from 'lucide-react';
import type { PreChargeBlockRequestDto, PreChargeBlockResponseDto, PublicDonationPageDto, SimulateDonationInput } from '@signalvote/shared';
import {
  AddressAutocomplete,
  type AddressAutocompleteValue,
} from '@/components/finance/shared/classification';
import {
  checkContributionLimit,
  createDonationCharge,
  type ChargeFeedback,
  type DonationChargeResult,
} from '@/lib/publicDonationApi';
import { DonationAmountButtons } from './DonationAmountButtons';
import { DonationHeaderMessage } from './DonationHeaderMessage';

interface DonationFormProps {
  subsidiary: string;
  slug: string;
  page: PublicDonationPageDto;
  /**
   * The orchestrator classifies the charge result: it advances phase for the
   * page-level outcomes (`created` → checkout, `notReady` (409) → unavailable) and
   * returns the inline `ChargeFeedback` the form should surface for the rest — a
   * last-mile `blocked` (reuses the block UI) or a retryable error message. It also
   * receives the gift amount so the thank-you screen can show it.
   */
  onChargeResult: (result: DonationChargeResult, amount: number) => ChargeFeedback;
  /**
   * Demo mode (#319): on a demo campaign the form skips the Stripe block+charge path entirely and
   * hands the collected donor fields up so the demo card widget's Donate can simulate the gift.
   */
  onDemoContinue?: (fields: SimulateDonationInput, amount: number) => void;
}

const MIN_AMOUNT = 1;

const STEPS = ['Amount', 'Details', 'Payment'] as const;

const emptyAddress: AddressAutocompleteValue = {
  address: '',
  addressLine2: '',
  city: '',
  state: '',
  zip: '',
};

// The soft-failure block: a hard "over the limit, no redesignation" that routes the donor to the
// treasurer. Used whenever we must block without a server decision to render — the pre-charge
// block-gate's non-ok path and a last-mile `blocked` charge that carried no decision.
const TREASURER_FALLBACK_BLOCK: PreChargeBlockResponseDto = {
  decision: PreChargeLimitDecision.Block,
  redesignationOffered: false,
  availableElections: [],
};

// Donor-facing labels read naturally mid-sentence ("supports the primary election"), so the radio
// options capitalize the same string rather than keeping a second, near-duplicate copy list.
function sentenceCase(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function DonationForm({ subsidiary, slug, page, onChargeResult, onDemoContinue }: DonationFormProps) {
  const hasElectionOptions = page.electionDesignationOptions.length > 0;
  // The suggested-gift presets, in the campaign's order — the campaign's custom set when it has one,
  // otherwise the defaults (never button-less). No preset is preselected: the donor picks one or types
  // an "other amount", which gates Continue.
  const suggestedAmounts = useMemo(() => resolveDonationAmounts(page.customAmounts), [page.customAmounts]);

  const [amount, setAmount] = useState('');
  // The designation the donor MADE THEMSELVES — `Undesignated` until they pick one. The page
  // discloses an election either way but only ASKS through the change affordance, so seeding this
  // with the disclosed value would submit a designation the donor never made. The server attributes
  // an undesignated gift to the next election for limit purposes (ElectionBucketResolver,
  // 11 CFR 110.1(b)(2)(ii)) and deliberately does not write that back, because recording a
  // designation the contributor never made is a redesignation the committee must disclose. Holding
  // the two apart is what lets the cure path tell a contributor's own written designation from one
  // the system defaulted.
  const [donorDesignatedElection, setDonorDesignatedElection] = useState<ElectionDesignation>(
    ElectionDesignation.Undesignated,
  );
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [occupation, setOccupation] = useState('');
  const [employer, setEmployer] = useState('');
  // Retired / not-currently-employed: submits NOT_EMPLOYED_VALUE for both occupation and employer
  // and disables those inputs, so the donor never has to invent an employer they don't have.
  const [notEmployed, setNotEmployed] = useState(false);
  const [address, setAddress] = useState<AddressAutocompleteValue>(emptyAddress);

  // Defaults ON (campaign's choice): the fee is shown transparently and the donor can opt out via the
  // "Edit" control, so most gifts arrive with the committee netting the full amount.
  const [coverFees, setCoverFees] = useState(true);
  // What the donor is SHOWN — their own designation when they made one, else the election the SERVER
  // says an undesignated gift joins. NOT what gets submitted, and deliberately not the options head:
  // that list is filtered to what the picker can express (Primary / General), so where a runoff is
  // next the head names an election the money will never count toward. Reading the server's own
  // resolver answer is what keeps the sentence and the ceiling naming the same election.
  const displayedElection =
    donorDesignatedElection !== ElectionDesignation.Undesignated
      ? donorDesignatedElection
      : page.resolvedElection;
  // Whether the "cover the fee" explainer/checkbox is expanded on the payment step (the "Edit" affordance).
  const [feeEditOpen, setFeeEditOpen] = useState(false);

  // Monthly recurring opt-in — defaults UNCHECKED (state law CA AB 775 / MD HB 17 + card-network rules
  // require an unchecked recurring box). Only offered when the server says recurring is available (the
  // per-campaign flag is on AND a still-future election gives a cancel_at cutoff).
  const [recurring, setRecurring] = useState(false);
  const canGiveMonthly = page.recurringEnabled && !page.isDemo;

  // The progressive flow: 0 Amount → 1 Details → 2 Payment. Attestations are shown as read-only
  // disclosures on the payment step and affirmed by submitting (no per-item checkbox gate).
  const [step, setStep] = useState(0);

  const [submitting, setSubmitting] = useState(false);
  // The block-gate outcome to surface (redesignation offer or treasurer fallback).
  const [block, setBlock] = useState<PreChargeBlockResponseDto | null>(null);
  // A retryable failure message from the charge (rate-limited / processing error).
  const [error, setError] = useState<string | null>(null);

  const parsedAmount = Number(amount);
  const amountValid = Number.isFinite(parsedAmount) && parsedAmount >= MIN_AMOUNT;

  // Employment is required (or the retired/unemployed box). The self-employed case is handled by the
  // disclosure copy, not by making the fields optional.
  const employmentComplete = notEmployed || (occupation.trim() !== '' && employer.trim() !== '');
  const donorIdentityComplete =
    firstName.trim() !== '' && lastName.trim() !== '' && email.trim() !== '';
  // The home address is gated for the same reason as employment: it is reportable data, a gift is
  // itemized against it, and unlike the other fields it CANNOT be recovered after the fact — an
  // address the donor typed but never committed only ever existed in their browser, so it reaches no
  // log, no Stripe metadata and no receipt. The whole address is required, not just the street line,
  // because a partial one does not itemize.
  const addressComplete =
    address.address.trim() !== '' &&
    address.city.trim() !== '' &&
    address.state.trim() !== '' &&
    address.zip.trim() !== '';
  const detailsComplete = donorIdentityComplete && employmentComplete && addressComplete;

  const step0Done = amountValid;
  const step1Done = step0Done && detailsComplete;
  const reachable = [true, step0Done, step0Done && step1Done];

  // The gift the donor typed, and the grossed-up total when covering the fee. `potentialFee` is the
  // gross-up delta computed regardless of the toggle, so the "Edit" explainer can name the exact fee
  // ($X) even before the donor opts in.
  const intendedCents = amountValid ? Math.round(parsedAmount * 100) : 0;
  const grossedCents = amountValid
    ? coverFeesChargeCents(intendedCents, page.feeRate, page.feeFlatCents)
    : intendedCents;
  const chargedCents = coverFees ? grossedCents : intendedCents;
  const potentialFeeDollars = ((grossedCents - intendedCents) / 100).toFixed(2);
  const intendedDollars = (intendedCents / 100).toFixed(2);
  const chargedTotalDollars = (chargedCents / 100).toFixed(2);

  const resolvedOccupation = notEmployed ? NOT_EMPLOYED_VALUE : occupation.trim() || undefined;
  const resolvedEmployer = notEmployed ? NOT_EMPLOYED_VALUE : employer.trim() || undefined;

  function donorFields(): PreChargeBlockRequestDto {
    return {
      amount: parsedAmount,
      electionDesignation: donorDesignatedElection,
      firstName: firstName || null,
      lastName: lastName || null,
      email: email || null,
      addressLine1: address.address || null,
      addressLine2: address.addressLine2 || null,
      city: address.city || null,
      state: address.state || null,
      zip: address.zip || null,
    };
  }

  function demoFields(): SimulateDonationInput {
    return {
      firstName,
      lastName,
      email,
      address: address.address || null,
      addressLine2: address.addressLine2 || null,
      city: address.city || null,
      state: address.state || null,
      zipCode: address.zip || null,
      occupation: resolvedOccupation ?? null,
      employer: resolvedEmployer ?? null,
      amount: parsedAmount,
      electionDesignation: donorDesignatedElection,
    };
  }

  function clearOutcomes() {
    setBlock(null);
    setError(null);
  }

  function goToStep(next: number) {
    if (!reachable[next]) return;
    clearOutcomes();
    setStep(next);
  }

  function advance() {
    if (step === 0 && amountValid) return goToStep(1);
    if (step === 1 && detailsComplete) return goToStep(2);
  }

  async function handleDonate() {
    if (!amountValid || !detailsComplete || submitting) return;

    // Demo mode: no Stripe block/charge — hand the collected donor fields up to the demo card widget.
    if (page.isDemo) {
      onDemoContinue?.(demoFields(), parsedAmount);
      return;
    }

    setSubmitting(true);
    clearOutcomes();

    // 1) Block-gate: always call block BEFORE charge.
    const blockResult = await checkContributionLimit(subsidiary, slug, donorFields());
    if (blockResult.status !== 'ok') {
      // Missing/rate-limited/error — surface a soft failure, don't charge.
      setSubmitting(false);
      setBlock(TREASURER_FALLBACK_BLOCK);
      return;
    }

    // Exhaustive on purpose: the fall-through from here is CHARGING THE CARD, so a decision value
    // added later must fail the build rather than quietly bill a donor the server meant to stop.
    const decision = blockResult.decision;
    switch (decision.decision) {
      case PreChargeLimitDecision.Block:
      case PreChargeLimitDecision.AcceptOverLimit:
        // Stop here and put the decision on screen. A Block is a refusal and ends the flow; an
        // AcceptOverLimit is a pause, not a refusal — the panel it renders carries a "continue"
        // affordance that resumes at the charge below. Either way the donor sees the election with
        // headroom first, because taking that offer is an express designation needing no cure at all.
        setSubmitting(false);
        setBlock(decision);
        return;
      case PreChargeLimitDecision.Allow:
        break;
      default:
        return assertNever(decision.decision);
    }

    await chargeNow();
  }

  // The charge itself. Reached either straight off a clean block-gate or from the donor choosing to
  // continue with an accepted over-limit gift — one path, so the two can never diverge on what is
  // sent or how the outcome is classified.
  async function chargeNow() {
    setSubmitting(true);

    // 2) Charge with every required attestation affirmed — clicking Donate is the affirmation now that
    //    the rules are shown as disclosures rather than per-item checkboxes.
    const chargeResult = await createDonationCharge(subsidiary, slug, {
      ...donorFields(),
      occupation: resolvedOccupation ?? null,
      employer: resolvedEmployer ?? null,
      affirmedAttestations: page.requiredAttestationKeys,
      coverFees,
      recurring: canGiveMonthly && recurring,
    });
    setSubmitting(false);

    // The page classifies the outcome: it handles the page-level transitions
    // (checkout / unavailable) itself and returns what to surface inline here.
    const feedback = onChargeResult(chargeResult, parsedAmount);
    if (feedback.kind === 'blocked') {
      // A null block (server withheld the decision) falls back to the treasurer path,
      // mirroring the pre-charge block-gate's own soft-failure behavior.
      setBlock(feedback.block ?? TREASURER_FALLBACK_BLOCK);
    } else if (feedback.kind === 'error') {
      setError(feedback.message);
    }
  }

  function applyRedesignation(option: ElectionDesignation) {
    setDonorDesignatedElection(option);
    setBlock(null);
  }

  // The donor declined the offer (or had none to take) on an accepted over-limit gift. The money
  // lands and the treasurer cures it — refusing it here is what #744 removed.
  async function continueWithOverLimitGift() {
    setBlock(null);
    await chargeNow();
  }

  // Is there actually another election to move this gift to? Drives which panel renders, and it is
  // the two facts together — an offer with no elections in it is not an offer.
  const blockHasOffer = !!block && block.redesignationOffered && block.availableElections.length > 0;

  // One button, two placements: alongside the redesignation options, or alone when there are none.
  function ContinueAnyway({ subtle = false }: { subtle?: boolean }) {
    return (
      <button
        type="button"
        onClick={continueWithOverLimitGift}
        disabled={submitting}
        className={
          subtle
            ? 'text-sm underline text-muted-foreground hover:text-foreground disabled:opacity-50'
            : 'rounded-md border border-border px-3 py-1 text-sm hover:bg-accent disabled:opacity-50'
        }
      >
        Continue with my gift as it is
      </button>
    );
  }

  const accent = { color: 'var(--color-primary)' } as const;

  return (
    <div className="donation-flow space-y-5 pb-28">
      <header className="donation-heading">
        {page.logoUrl ? (
          // The campaign's uploaded logo, at the top of the form, constrained to a max height preserving
          // aspect ratio. Replaces the committee-initial mark when present (additive branding — the
          // committee name + legal disclaimers below are unaffected, ADR-052).
          <img
            data-testid="donation-logo"
            src={page.logoUrl}
            alt={`${page.committeeName} logo`}
            className="donation-logo mx-auto max-h-20 w-auto object-contain"
          />
        ) : (
          <div className="donation-mark" aria-hidden="true">
            {page.committeeName.slice(0, 1).toUpperCase()}
          </div>
        )}
        <p className="donation-eyebrow">Support the campaign</p>
        <h1>Donate to {page.committeeName}</h1>
      </header>

      {/* The campaign's optional personal appeal, at the top of the form above the amount buttons.
          Escaped plain text, newlines preserved; renders nothing when the campaign set no header. */}
      <DonationHeaderMessage
        message={page.headerMessage}
        className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm leading-relaxed"
      />

      {/* Progress: three steps, checked as completed. Earlier reached steps are clickable to edit. */}
      <nav className="flex border-b border-border" aria-label="Progress">
        {STEPS.map((label, i) => {
          const active = i === step;
          // min-w-0 lets each step share the row equally instead of holding its label's full width;
          // the label span then wraps/breaks rather than pushing the row (and the whole layout
          // viewport) wider than the screen at narrow widths or large accessibility text.
          return (
            <button
              key={label}
              type="button"
              disabled={!reachable[i]}
              onClick={() => goToStep(i)}
              className="flex min-w-0 flex-1 items-center justify-center gap-1.5 pb-3 text-sm font-bold disabled:opacity-40"
              style={{
                color: active ? 'var(--color-primary)' : undefined,
                borderBottom: active ? '2px solid var(--color-primary)' : '2px solid transparent',
                marginBottom: '-1px',
              }}
              aria-current={active ? 'step' : undefined}
            >
              {reachable[i] && i < step && (
                <Check aria-hidden="true" className="h-4 w-4 shrink-0" style={{ color: 'var(--color-success)' }} />
              )}
              <span className="min-w-0 break-words">{i + 1}. {label}</span>
            </button>
          );
        })}
      </nav>

      {/* --- Step 1: amount + recurring ------------------------------------ */}
      {step === 0 && (
        <section className="donation-panel space-y-5" aria-labelledby="gift-heading">
          <div>
            <h2 id="gift-heading" className="donation-section-title">Choose your gift</h2>
            <p className="donation-section-description">Every amount makes a meaningful difference.</p>
          </div>

          <DonationAmountButtons amounts={suggestedAmounts} selected={amount} onSelect={setAmount} />

          <div className="space-y-1.5">
            <label htmlFor="donation-amount" className="donation-label">Other amount</label>
            <div className="donation-money-input">
              <span aria-hidden="true">$</span>
              <input
                id="donation-amount"
                type="number"
                min={MIN_AMOUNT}
                step="1"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
              />
            </div>
          </div>

          {hasElectionOptions && (
            /* GitHub #729 item 1 + #744 — a stated fact, and nothing to answer. It began as a
               labelled select between the amount and the monthly toggle, asking the overwhelming
               majority of donors a contribution-limit accounting question they cannot answer at the
               moment they have already decided to give; #729 demoted it to a quiet "Change which
               election" affordance, and it is now gone entirely. Donors expect the campaign to
               handle the allocation, and nobody in the industry asks: ActBlue and WinRed put no
               election choice in front of a donor and disclose it in fine print.

               Removing it is what keeps every one-time gift UNDESIGNATED, which is the precondition
               for the cheap presumptive cure (11 CFR 110.1(b)(5)(ii)(B) — a notice, no signature).
               A picked election that the gift then exceeded was the one way this page could produce
               a designated over-limit row, whose only cures are a refund or a chased signature.
               The donor can still designate, but only where it helps them: the over-limit offer
               below, which lists nothing the gift would not fit. */
            <div className="space-y-2 border-t border-border pt-4 text-sm text-muted-foreground">
              <span>
                Your gift supports{' '}
                <strong className="text-foreground">{donorElectionLabel(displayedElection)}</strong>.
              </span>
            </div>
          )}

          {canGiveMonthly && (
            <label className="donation-fee-option">
              <input
                id="donation-recurring"
                type="checkbox"
                checked={recurring}
                onChange={(e) => setRecurring(e.target.checked)}
              />
              <span>
                <strong>Give monthly</strong>
                <span>Make this a recurring gift, charged once a month.</span>
                {recurring && (
                  <span>
                    {amountValid ? `You'll give $${parsedAmount.toFixed(2)} each month. ` : ''}
                    {RECURRING_DONATION_DISCLOSURE} You can cancel anytime
                    {page.treasurerEmail ? (
                      <> by contacting the campaign at <a className="underline" href={`mailto:${page.treasurerEmail}`}>{page.treasurerEmail}</a>.</>
                    ) : (
                      ' by contacting the campaign.'
                    )}
                  </span>
                )}
              </span>
            </label>
          )}
        </section>
      )}

      {/* --- Step 2: details + employment --------------------------------- */}
      {step === 1 && (
        <section className="donation-panel space-y-4" aria-labelledby="details-heading">
          <div>
            <h2 id="details-heading" className="donation-section-title">Your details</h2>
            <p className="donation-section-description">Required for contribution reporting.</p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label htmlFor="donation-first-name" className="donation-label">First name</label>
              <input id="donation-first-name" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="donation-control" autoComplete="given-name" />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="donation-last-name" className="donation-label">Last name</label>
              <input id="donation-last-name" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} className="donation-control" autoComplete="family-name" />
            </div>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="donation-email" className="donation-label">Email</label>
            <input id="donation-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="donation-control" autoComplete="email" />
          </div>
          <div className="space-y-1.5">
            <label className="donation-label">Home address</label>
            {/* alwaysShowFields: the address is required here, and on mobile inside the campaign's
                embed iframe the suggestion dropdown opens into a clipped region the donor cannot
                reach — so it must never be the only way in. */}
            <AddressAutocomplete value={address} onChange={setAddress} tokenSource="public" tokenSubsidiary={subsidiary} idPrefix="donation-address" alwaysShowFields />
            {/* Typing in the box does not record anything on its own — the address is only captured
                when a suggestion is picked or the manual option is taken. Without saying so, a donor
                who typed their address sees filled-in text next to a disabled button and no reason why. */}
            <p data-testid="donation-address-hint" className="donation-section-description">
              Pick your address from the list, or choose &quot;Add … manually&quot;, so we can record it
              for reporting.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label htmlFor="donation-occupation" className="donation-label">Occupation</label>
              <input id="donation-occupation" type="text" value={notEmployed ? '' : occupation} disabled={notEmployed} onChange={(e) => setOccupation(e.target.value)} className="donation-control" autoComplete="organization-title" style={notEmployed ? { opacity: 0.5 } : undefined} />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="donation-employer" className="donation-label">Employer</label>
              <input id="donation-employer" type="text" value={notEmployed ? '' : employer} disabled={notEmployed} onChange={(e) => setEmployer(e.target.value)} className="donation-control" autoComplete="organization" style={notEmployed ? { opacity: 0.5 } : undefined} />
            </div>
          </div>
          <label className="donation-attestation" style={{ cursor: 'pointer' }}>
            <input type="checkbox" checked={notEmployed} onChange={(e) => setNotEmployed(e.target.checked)} />
            <span>I&apos;m retired or currently unemployed</span>
          </label>
          <p className="donation-section-description">{EMPLOYMENT_DISCLOSURE}</p>
        </section>
      )}

      {/* --- Step 3: payment (fee opt-in + disclosures) -------------------- */}
      {step === 2 && (
        <section className="donation-panel space-y-5" aria-labelledby="payment-heading">
          {!block && (
            <>
              <p id="payment-heading" className="donation-section-description">
                Complete your <strong className="text-base text-foreground">${intendedDollars}</strong> contribution:
              </p>

              <div className="space-y-2">
                <div className="text-3xl font-bold leading-none">${intendedDollars}</div>
                <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                  {coverFees ? (
                    <span>+ ${potentialFeeDollars} processing fee</span>
                  ) : (
                    <span>Cover the ${potentialFeeDollars} processing fee?</span>
                  )}
                  <button
                    type="button"
                    onClick={() => setFeeEditOpen((v) => !v)}
                    className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-medium"
                    style={accent}
                    aria-expanded={feeEditOpen}
                  >
                    <Pencil aria-hidden="true" className="h-3 w-3" /> Edit
                  </button>
                </div>
                {feeEditOpen && (
                  <div className="rounded-lg border border-border p-3 text-sm">
                    <p className="text-muted-foreground">
                      By covering the <strong className="text-foreground">${potentialFeeDollars}</strong> processing fee, 100% of your contribution will go to {page.committeeName}.
                    </p>
                    <label className="donation-attestation mt-2" style={{ cursor: 'pointer' }}>
                      <input
                        id="donation-cover-fees"
                        type="checkbox"
                        checked={coverFees}
                        onChange={(e) => setCoverFees(e.target.checked)}
                      />
                      <span className="font-bold">I want to cover fees</span>
                    </label>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Block-gate outcomes — never a dollar figure, never PII. */}
          {block && blockHasOffer && (
            <div className="rounded-md border border-border bg-muted/30 p-3 text-sm space-y-2">
              <p>
                You&apos;ve reached the limit for {donorElectionLabel(displayedElection)}. You can
                apply this gift to a different election instead:
              </p>
              <div className="flex flex-wrap gap-2">
                {block.availableElections.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => applyRedesignation(opt)}
                    className="rounded-md border border-border px-3 py-1 text-sm hover:bg-accent"
                  >
                    Apply this gift to {donorElectionLabel(opt)}
                  </button>
                ))}
              </div>
              {block.decision === PreChargeLimitDecision.AcceptOverLimit && <ContinueAnyway subtle />}
            </div>
          )}
          {block?.decision === PreChargeLimitDecision.AcceptOverLimit && !blockHasOffer && (
            // Nowhere left to move the money — but still not a dead end. The committee accepts it
            // and its treasurer resolves it, refunding what cannot be applied anywhere.
            <div className="rounded-md border border-border bg-muted/30 p-3 text-sm space-y-2">
              <p>
                This gift goes past the limit for {donorElectionLabel(displayedElection)}. You can
                still give it — the campaign&apos;s treasurer will be in touch about how it&apos;s
                applied, and you can ask for it back.
              </p>
              <ContinueAnyway />
            </div>
          )}
          {block && block.decision !== PreChargeLimitDecision.AcceptOverLimit && !blockHasOffer && (
            <p className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
              This gift would exceed the contribution limit. Please reduce the amount
              {page.treasurerEmail ? (
                <>
                  {' '}or contact the campaign at{' '}
                  <a className="underline" href={`mailto:${page.treasurerEmail}`}>
                    {page.treasurerEmail}
                  </a>
                  .
                </>
              ) : (
                '.'
              )}
            </p>
          )}

          {/* A retryable charge failure (rate-limited / processing error) — never a $ or PII. */}
          {error && !block && (
            <p role="alert" className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
              {error}
            </p>
          )}

          {/* Attestations as read-only disclosures — donating affirms them. */}
          {!block && page.requiredAttestationKeys.length > 0 && (
            <div className="space-y-2 border-t border-border pt-4">
              <p className="donation-label">By donating, you affirm:</p>
              <ol className="list-decimal space-y-1.5 pl-5 text-sm text-muted-foreground">
                {page.requiredAttestationKeys.map((key) => (
                  <li key={key}>{attestationCopy(key)}</li>
                ))}
              </ol>
            </div>
          )}
        </section>
      )}

      {/* --- Sticky action -------------------------------------------------- */}
      <div className="donation-sticky-action">
        <div className="donation-shell flex gap-3">
          {step > 0 && !block && (
            <button type="button" onClick={() => goToStep(step - 1)} className="rounded-md border border-border px-5 text-sm font-bold" style={{ minHeight: '3.25rem' }}>
              Back
            </button>
          )}
          {step < 2 ? (
            <button
              type="button"
              onClick={advance}
              disabled={step === 0 ? !amountValid : !detailsComplete}
              className="donation-submit flex-1"
            >
              Continue
            </button>
          ) : (
            <button
              type="button"
              onClick={handleDonate}
              disabled={submitting || !!block || !amountValid || !detailsComplete}
              className="donation-submit flex-1"
            >
              {submitting
                ? 'Please wait…'
                : block
                  ? 'Update your gift to continue'
                  : `Donate $${chargedTotalDollars}${recurring ? '/mo' : ''}`}
            </button>
          )}
        </div>
        <p><LockKeyhole aria-hidden="true" /> Your payment information is handled securely.</p>
      </div>
    </div>
  );
}
